/**
 * Hook that powers the command palette search.
 *
 * Builds a flat, permission-filtered list of searchable items from
 * `useVisibleSections()` (which already handles role/feature/permission/app
 * checks), then runs the fuzzy search engine against user input.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useVisibleSections, type VisibleSection } from "./useVisibleSections.js";
import type { NavGroup, NavLinkItem } from "../nav/navConfig.js";
import { searchMetadata } from "../lib/search/searchIndex.js";
import { contentSearchMetadata, contentSearchAnchors } from "../lib/search/searchRegistry.js";
import { searchItems, type SearchableItem, type SearchResult } from "../lib/search/searchEngine.js";
import { isAdminUser } from "../lib/permissions.js";
import { useMe } from "./useMe.js";

export type { SearchResult } from "../lib/search/searchEngine.js";

/**
 * Recursively flatten groups into searchable items, including groups
 * that have their own `path` (navigable group headers like Streaming Dashboard).
 *
 * `ancestorLabels` carries the full chain of parent group labels so breadcrumbs
 * render correctly (e.g., "Dashboard > Streaming Dashboard > Control Center").
 */
function flattenGroup(
  group: NavGroup,
  sectionId: string,
  sectionLabel: string,
  ancestorLabels: string[] = [],
  headerId?: string,
  headerLabel?: string
): SearchableItem[] {
  const items: SearchableItem[] = [];

  // The command-palette category header is the OUTERMOST navigable ancestor
  // group: once an ancestor with a `path` claims it, it sticks for the whole
  // subtree (so Streaming Dashboard's nested Control Center items still head as
  // "Streaming Dashboard"); otherwise a navigable group claims it for itself and
  // its descendants. A non-navigable group leaves it undefined, so its items
  // keep the section header.
  const effectiveHeaderId = headerId ?? (group.path ? group.id : undefined);
  const effectiveHeaderLabel = headerLabel ?? (group.path ? group.label : undefined);

  // If the group itself is navigable (has a path), add it as a searchable item
  if (group.path) {
    const groupAsItem: NavLinkItem = {
      id: group.id,
      label: group.label,
      shortLabel: group.shortLabel,
      path: group.path,
      icon: group.icon ?? "lucide:folder"
    };
    items.push({
      item: groupAsItem,
      sectionId,
      sectionLabel,
      groupLabel: ancestorLabels.length ? ancestorLabels.join(" > ") : undefined,
      headerId: effectiveHeaderId,
      headerLabel: effectiveHeaderLabel
    });
  }

  // Build the label chain for items inside this group
  const itemAncestors = [...ancestorLabels, group.label];
  const itemGroupLabel = itemAncestors.join(" > ");

  // Add group items
  for (const item of group.items) {
    items.push({
      item,
      sectionId,
      sectionLabel,
      groupLabel: itemGroupLabel,
      headerId: effectiveHeaderId,
      headerLabel: effectiveHeaderLabel
    });
  }

  // Recurse into child groups with the full ancestor chain + the sticky header
  if (group.groups) {
    for (const child of group.groups) {
      items.push(...flattenGroup(child, sectionId, sectionLabel, itemAncestors, effectiveHeaderId, effectiveHeaderLabel));
    }
  }

  return items;
}

/**
 * Flatten a visible section into searchable items.
 */
function flattenSection(section: VisibleSection): SearchableItem[] {
  const items: SearchableItem[] = [];

  // Direct items in the section
  if (section.items) {
    for (const item of section.items) {
      // Skip items that are also represented as group children (avoid duplicates)
      // The section.items array from useVisibleSections already contains
      // flattened group items, so we only add direct items that aren't
      // duplicated in groups.
      if (!section.groups) {
        items.push({
          item,
          sectionId: section.id,
          sectionLabel: section.label
        });
      }
    }
  }

  // Flatten groups (this is our primary source when groups exist)
  if (section.groups) {
    for (const group of section.groups) {
      items.push(...flattenGroup(group, section.id, section.label));
    }

    // Also add direct items that are NOT part of any group
    if (section.items) {
      const groupItemIds = new Set<string>();
      const collectIds = (groups: NavGroup[]) => {
        for (const g of groups) {
          // Include the group itself if navigable
          if (g.path) groupItemIds.add(g.id);
          for (const gi of g.items) groupItemIds.add(gi.id);
          if (g.groups) collectIds(g.groups);
        }
      };
      collectIds(section.groups);

      for (const item of section.items) {
        if (!groupItemIds.has(item.id)) {
          items.push({
            item,
            sectionId: section.id,
            sectionLabel: section.label
          });
        }
      }
    }
  }

  return items;
}

/**
 * Pages that exist as routes but are NOT in the sidebar navConfig.
 * These are "deep" pages accessed through other pages (e.g., Media Portal
 * is reached via Control Center, Server Users via Users & Access domain).
 * We include them here so they appear in search results.
 *
 * Each entry specifies the NavLinkItem data plus the section/group context
 * for breadcrumb display.  Permission gating is handled by the `adminOnly`
 * flag — when true the item is only added for admin users.
 */
const DEEP_PAGES: Array<{
  item: NavLinkItem;
  sectionId: string;
  sectionLabel: string;
  groupLabel?: string;
  adminOnly?: boolean;
  requiresFeature?: "wsdashboard" | "webconsole";
}> = [
  // NOTE: the Asset Management Center (`content-media-library`) is NOT listed
  // here. It lives in the `content` nav section as a navigable group, so it is
  // already surfaced through `flattenSection(visibleSections)` — gated on the
  // AMC capability (admin OR `media.library.use`) by `useVisibleSections`.
  // A DEEP_PAGES duplicate would be `adminOnly`, wrongly hiding it from ACL
  // users and double-counting for admins (deduped by id anyway).
  {
    item: {
      id: "users-registration",
      label: "User Registration",
      path: "/users/registration",
      icon: "lucide:user-plus"
    },
    sectionId: "user-management",
    sectionLabel: "User Management",
    adminOnly: true
  }
];

/**
 * Admin suggestion IDs shown when search is opened with no query.
 */
const ADMIN_SUGGESTION_IDS = [
  "system-dashboard",
  "streaming-dashboard",
  "settings-general",
  "users-admin",
  "system-ssl-control"
];

/**
 * Regular user suggestion IDs shown when search is opened with no query.
 */
const USER_SUGGESTION_IDS = [
  "app-dashboard",
  "profile-account"
];

export function useAppSearch() {
  const { visibleSections, fallbackSection, isAdmin } = useVisibleSections();
  const { user, appConfig } = useMe();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isStreamingEnabled = appConfig?.features?.wsdashboardEnabled ?? false;
  const isWebconsoleEnabled = appConfig?.features?.webconsoleEnabled ?? false;
  const isTransferEnabled = appConfig?.features?.interInstanceTransferEnabled ?? false;
  const isDeleteProtectionEnabled = appConfig?.features?.deleteProtectionEnabled ?? false;

  // Merged metadata: nav-level + content-level search metadata
  const mergedMetadata = useMemo(() => {
    return { ...searchMetadata, ...contentSearchMetadata };
  }, []);

  // Build flat searchable items from visible sections + fallback (account/profile) + deep pages + content anchors
  const searchableItems = useMemo<SearchableItem[]>(() => {
    const items: SearchableItem[] = [];

    for (const section of visibleSections) {
      items.push(...flattenSection(section));
    }

    // Always add fallback section (profile/account items available to all users)
    items.push(...flattenSection(fallbackSection));

    // Add deep pages (routes not in navConfig but still searchable)
    for (const deep of DEEP_PAGES) {
      if (deep.adminOnly && !isAdminUser(user)) continue;
      if (deep.requiresFeature === "wsdashboard" && !isStreamingEnabled) continue;
      if (deep.requiresFeature === "webconsole" && !isWebconsoleEnabled) continue;
      items.push({
        item: deep.item,
        sectionId: deep.sectionId,
        sectionLabel: deep.sectionLabel,
        groupLabel: deep.groupLabel
      });
    }

    // Add content-level search anchors (individual settings within sections)
    for (const anchor of contentSearchAnchors) {
      if (anchor.adminOnly && !isAdminUser(user)) continue;
      if (anchor.requiresFeature === "wsdashboard" && !isStreamingEnabled) continue;
      if (anchor.requiresFeature === "webconsole" && !isWebconsoleEnabled) continue;
      if (anchor.requiresFeature === "inter-instance-transfer" && !isTransferEnabled) continue;
      if (anchor.requiresFeature === "delete-protection" && !isDeleteProtectionEnabled) continue;
      items.push({
        item: {
          id: anchor.id,
          label: anchor.label,
          path: anchor.path,
          icon: anchor.icon
        },
        sectionId: anchor.sectionId,
        sectionLabel: anchor.sectionLabel,
        groupLabel: anchor.groupLabel
      });
    }

    // Deduplicate by item.id (groups with path may appear as both group + item)
    const seen = new Set<string>();
    return items.filter((entry) => {
      if (seen.has(entry.item.id)) return false;
      seen.add(entry.item.id);
      return true;
    });
  }, [visibleSections, fallbackSection, user, isStreamingEnabled, isWebconsoleEnabled, isTransferEnabled, isDeleteProtectionEnabled]);

  // Search results — sorted into section-grouped order so the flat array
  // matches the render order in AppSearch.tsx (which groups by sectionId).
  // Without this, selectedIndex from mouse hover (grouped order) would
  // desync from keyboard Enter (flat order), navigating to wrong pages.
  const results = useMemo<SearchResult[]>(() => {
    const trimmed = query.trim();
    if (!trimmed) return [];
    const raw = searchItems(trimmed, searchableItems, mergedMetadata);

    // Group by the category header key (headerId ?? sectionId) in order of first
    // appearance, preserving score order within each header. MUST match the key
    // groupResultsBySection uses in AppSearch.tsx, or the flat keyboard order
    // desyncs from the grouped mouse order.
    const headerOrder: string[] = [];
    const byHeader = new Map<string, SearchResult[]>();
    for (const r of raw) {
      const key = r.headerId ?? r.sectionId;
      let list = byHeader.get(key);
      if (!list) {
        list = [];
        byHeader.set(key, list);
        headerOrder.push(key);
      }
      list.push(r);
    }
    return headerOrder.flatMap((id) => byHeader.get(id)!);
  }, [query, searchableItems]);

  // Suggestions (empty query state)
  const suggestions = useMemo<SearchResult[]>(() => {
    const ids = isAdmin ? ADMIN_SUGGESTION_IDS : USER_SUGGESTION_IDS;
    const items: SearchResult[] = [];

    for (const id of ids) {
      const found = searchableItems.find((si) => si.item.id === id);
      if (found) {
        const meta = searchMetadata[id];
        items.push({
          ...found,
          description: meta?.description ?? "",
          score: 100
        });
      }
    }

    return items;
  }, [isAdmin, searchableItems]);

  // The list that the UI currently displays
  const displayedResults = query.trim() ? results : suggestions;

  // Reset selection when results change
  const prevResultsRef = useRef(displayedResults);
  if (prevResultsRef.current !== displayedResults) {
    prevResultsRef.current = displayedResults;
    setSelectedIndex(0);
  }

  const handleNavigate = useCallback(
    (path: string, external?: boolean) => {
      setOpen(false);
      setQuery("");
      setSelectedIndex(0);

      if (external) {
        window.open(path, "_blank", "noopener,noreferrer");
      } else {
        // navigate() unconditionally for internal paths. React Router 8 only
        // subscribes to popstate, so a direct hash assignment never updates
        // useLocation(); routing through navigate() lets the shell useHashScroll
        // hook scroll, focus and highlight the target for same-page and
        // cross-page hits alike.
        navigate(path);
      }
    },
    [navigate]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery("");
    setSelectedIndex(0);
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev < displayedResults.length - 1 ? prev + 1 : 0
          );
          break;
        }
        case "ArrowUp": {
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : displayedResults.length - 1
          );
          break;
        }
        case "Enter": {
          event.preventDefault();
          const selected = displayedResults[selectedIndex];
          if (selected) {
            handleNavigate(selected.item.path, selected.item.external);
          }
          break;
        }
        case "Escape": {
          event.preventDefault();
          handleClose();
          break;
        }
      }
    },
    [open, displayedResults, selectedIndex, handleNavigate, handleClose]
  );

  return {
    query,
    setQuery,
    results: displayedResults,
    selectedIndex,
    setSelectedIndex,
    open,
    setOpen,
    handleOpen,
    handleClose,
    handleNavigate,
    onKeyDown,
    containerRef,
    inputRef,
    isEmptyQuery: !query.trim(),
    hasResults: displayedResults.length > 0,
    isAdmin,
    isStreamingEnabled
  };
}
